import { Box, Button, Card, Divider, Page, Tabs, Text } from '@shopify/polaris';
import { useMemo, useState } from 'react';
import { useCurrency } from '../../../customHooks/useCurrency';
import { useTimezone } from '../../../customHooks/useTimezone';
import { VerticalSpace } from '../../UI/Reports/VerticalSpace';
import { ActiveFilterChips } from './efficacy/ActiveFilterChips';
import { AnalysisView } from './efficacy/AnalysisView';
import { DiscountScale } from './efficacy/DiscountScale';
import { EfficacyHighlights } from './efficacy/EfficacyHighlights';
import { EfficacySummary } from './efficacy/EfficacySummary';
import { WarehouseRadar } from './efficacy/WarehouseRadar';
import { ExportMenu } from './efficacy/ExportMenu';
import { FiltersCard } from './efficacy/FiltersCard';
import { SeriesTable } from './efficacy/SeriesTable';
import { buildYearOptions, daysUntil, endOfSeason } from './efficacy/constants';
import { formatMoney, formatPct, formatUnits } from './efficacy/formatters';
import { useEfficacyData } from './efficacy/useEfficacyData';
import { useEfficacyFilters } from './efficacy/useEfficacyFilters';
import HeadSection from '../../UI/Reports/HeadSection';
import TotalizeNumbers from '../../UI/Reports/TotalizeNumbers';

const TABS = [
  { id: 'series', content: 'Análisis por Series' },
  { id: 'analysis', content: 'Análisis por Categoría' },
  { id: 'summary', content: 'Resumen' },
];

/**
 * Report de Eficacia Comercial.
 *
 * Composición:
 *   - `useEfficacyFilters` — todo el estado de filtros + hidratación
 *     de catálogos + sync con URL.
 *   - `useEfficacyData`    — fetch del informe; sort interno.
 *   - `<FiltersCard>`      — UI del bloque de filtros (Card aparte).
 *   - `<ActiveFilterChips>`— chips-resumen bajo el título.
 *   - `<ExportMenu>`       — popover "Exportar" del header.
 *   - Tabs:
 *       0. `<SeriesTable>`     (tabla detalle)
 *       1. `<AnalysisView>`    (radares + breakdown por categoría)
 *       2. `<EfficacySummary>` (KPI cards + pies + radar almacén)
 */
const ViewReportEfficacy = () => {
  const { timezone } = useTimezone();
  const { currency } = useCurrency();
  const yearOptions = useMemo(buildYearOptions, []);
  const fmt$ = (n) => formatMoney(n, currency);
  const pct = (n) => formatPct(n);

  const filterState = useEfficacyFilters();
  const { data, sortBy, sortDir, handleSort } = useEfficacyData(filterState);

  const profit = data?.metadata ? data.metadata.totalSellValuePvfTotal - data.metadata.totalBuyValue : null;

  const [tabIdx, setTabIdx] = useState(0);
  // `showSub` es GLOBAL al report: afecta tanto al tab Análisis (tabla
  // breakdown + radar) como al radar de almacén del Resumen. El toggle
  // vive arriba al lado de los Tabs.
  const [showSub, setShowSub] = useState(false);

  const exportParamsArgs = {
    season: filterState.season,
    year: filterState.year,
    validFilters: filterState.validFilters,
    depositsValue: filterState.depositsValue,
    depositRolesValue: filterState.depositRolesValue,
    depositGroupsValue: filterState.depositGroupsValue,
    familyQuery: filterState.familyQuery,
    currency,
    timezone,
  };

  const genderFilterActive = !!filterState.filters.genders?.value;

  // Etiqueta corta de campaña ("Verano 2025") para mostrar en
  // contextos como cabecera de la leyenda de cada radar.
  const campaignLabel = `${filterState.season === 'VERANO' ? 'Verano' : 'Invierno'} ${filterState.year}`;

  // KPIs del block "Estado de la campaña" — sólo válidos cuando hay
  // data. Filtramos familias sin entregas (sin `buyUnits` no hay
  // eficacia). La eficacia media es ponderada por unidades de compra
  // (sum(uds venta) / sum(uds compra)), igual que el TOTAL de la
  // tabla — así no infla series pequeñas con eficacia alta.
  const validEfficacyRows = (data?.data || []).filter(
    (r) => Number(r.buyUnits) > 0 && Number.isFinite(Number(r.efficacyTotal)),
  );
  const totalBuyUnits = validEfficacyRows.reduce((acc, r) => acc + (Number(r.buyUnits) || 0), 0);
  const totalSellUnits = validEfficacyRows.reduce((acc, r) => acc + (Number(r.sellUnitsTotal) || 0), 0);
  const meanEfficacy = totalBuyUnits > 0 ? totalSellUnits / totalBuyUnits : null;
  // Series "buenas": eficacia por encima del 60% — mismo umbral que el
  // cubo "Sanas + Agotadas" del SeriesStatsStrip.
  const goodEfficacyCount = validEfficacyRows.filter((r) => Number(r.efficacyTotal) > 0.6).length;
  const seasonEnd = endOfSeason(filterState.season, filterState.year);
  const daysLeft = daysUntil(seasonEnd);
  // Texto del KPI "Días para fin de temporada":
  //   · > 0  → "N días"
  //   · 0    → "Hoy"
  //   · < 0  → "Finalizada"
  const daysLeftLabel = daysLeft > 0 ? `${formatUnits(daysLeft)} días` : daysLeft === 0 ? 'Hoy' : 'Finalizada';

  // Ventas/día en temporada: uds vendidas en campaña ÷ días entre la
  // PRIMERA llegada de mercancía y hoy (si la campaña aún corre) o el
  // fin de temporada (si ya cerró). Es la velocidad media de venta.
  let firstArrivalDate = null;
  for (const r of data?.data || []) {
    if (!r.firstStoreEntryDate) continue;
    const d = new Date(r.firstStoreEntryDate);
    if (!firstArrivalDate || d < firstArrivalDate) firstArrivalDate = d;
  }
  const now = new Date();
  const seasonOpen = now < seasonEnd;
  const endRef = seasonOpen ? now : seasonEnd;
  const daysInSeason = firstArrivalDate
    ? Math.max(1, Math.ceil((endRef.getTime() - firstArrivalDate.getTime()) / (1000 * 60 * 60 * 24)))
    : null;
  const inSeasonUnits = Number(data?.metadata?.totalSellUnitsInSeason) || 0;
  const unitsPerDay = daysInSeason ? inSeasonUnits / daysInSeason : null;
  // Decimal con 1 dígito si el ritmo es bajo (< 10/día) — un entero
  // engaña cuando vendes 1.4 uds/día. A partir de 10 redondeamos.
  const unitsPerDayLabel =
    unitsPerDay == null
      ? '—'
      : unitsPerDay < 10
        ? `${unitsPerDay.toFixed(1)} uds/día`
        : `${formatUnits(Math.round(unitsPerDay))} uds/día`;

  const requiredExtra = data?.metadata?.totalBuyValue - data?.metadata?.totalSellValuePvfTotal || 0;
  const merchantValue = data?.metadata
    ? data.metadata.totalRemainUnits *
      (data.metadata.totalSellValuePvpTotal / 1.16 / Math.max(1, data.metadata.totalSellUnitsTotal))
    : 0; // Asumo IVA 16% MX para calcular el valor bruto de lo que queda en almacén a PVF.
  const neutralDiscount = 1 - requiredExtra / (merchantValue || 1); // Evito división por 0, aunque el caso ideal de 0 unidades restantes ya se cubre arriba.

  const neutralMsg = (
    <>
      <Text type="bodySm">
        {neutralDiscount < 0 ? (
          `La operación ya está en beneficio: El stock restante aporta directamente a las ganancias.`
        ) : (
          <>
            Punto neutro: Descuento del <b>{(neutralDiscount * 100).toFixed(1)}%</b> sobre el PVP.
          </>
        )}
      </Text>
    </>
  );

  return (
    <Page
      // fullWidth siempre: la tabla detalle necesita todo el ancho para
      // sus 20+ columnas. El cambio dinámico hacía re-mount de la Page
      // en Polaris 11. En Resumen ajustamos el ancho con un wrapper.
      fullWidth
      title="Eficacia Comercial"
      subtitle="Análisis de compras vs. ventas por serie-color"
      primaryAction={<ExportMenu disabled={!data} exportParamsArgs={exportParamsArgs} />}
    >
      {filterState.ready && (
        <>
          <FiltersCard
            yearOptions={yearOptions}
            season={filterState.season}
            setSeason={filterState.setSeason}
            year={filterState.year}
            setYear={filterState.setYear}
            depositsValue={filterState.depositsValue}
            depositRolesValue={filterState.depositRolesValue}
            depositGroupsValue={filterState.depositGroupsValue}
            setDepositScope={filterState.setDepositScope}
            depositOptions={filterState.depositOptions}
            groupOptions={filterState.groupOptions}
            filters={filterState.filters}
            handleFilter={filterState.handleFilter}
            clearFilters={filterState.clearFilters}
            familySearch={filterState.familySearch}
            setFamilySearch={filterState.setFamilySearch}
          />

          {data && (
            <>
              <VerticalSpace size="large" />
              <HeadSection
                title={`Temporada ${filterState.season === 'VERANO' ? 'Verano' : 'Invierno'} ${filterState.year}`}
                subtitle="Indicadores rápidos del estado de la campaña"
              />
              <VerticalSpace />
              {/* Card resumen: chips de filtros activos + 4 KPIs + barra
                  de tabs. El contenido de cada tab vive en sus propias
                  Cards bajo este bloque, separado visualmente. */}
              <Card>
                {/* Tabs a la izquierda + toggle "Mostrar subcategorías"
                    al extremo opuesto. El toggle es global al report:
                    afecta a la tabla por categoría del tab Análisis Y
                    al radar de almacén del Resumen. */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <Tabs tabs={TABS} selected={tabIdx} onSelect={setTabIdx} />
                  </div>
                  <Box paddingInlineEnd="3">
                    <Button primary={showSub} onClick={() => setShowSub((v) => !v)} size="slim">
                      {showSub ? 'Ocultar subcategorías' : 'Mostrar subcategorías'}
                    </Button>
                  </Box>
                </div>
                <Divider />
                <Box padding="4">
                  <ActiveFilterChips
                    filters={filterState.filters}
                    depositsValue={filterState.depositsValue}
                    depositRolesValue={filterState.depositRolesValue}
                    depositGroupsValue={filterState.depositGroupsValue}
                    depositOptions={filterState.depositOptions}
                    groupOptions={filterState.groupOptions}
                    familyQuery={filterState.familyQuery}
                  />
                  <Box minHeight=".75rem" />
                  <TotalizeNumbers
                    rows={[
                      { label: 'Vendido', value: `${formatUnits(data.metadata.totalSellUnitsTotal)} Uds.` },
                      {
                        label: 'Eficacia media',
                        value: meanEfficacy != null ? formatPct(meanEfficacy) : '—',
                      },
                      {
                        label: 'Eficacia > 60%',
                        value: `${formatUnits(goodEfficacyCount)} Serie${goodEfficacyCount === 1 ? '' : 's'}`,
                      },
                      { label: 'Ventas/día en temporada', value: unitsPerDayLabel },
                      { label: 'Días para fin de temporada', value: daysLeftLabel },
                    ]}
                  />
                </Box>
              </Card>

              {tabIdx === 0 && (
                <>
                  <Box minHeight=".5rem" />
                  <Card>
                    <Box padding="4">
                      <Text variant="headingMd" as="h3">
                        Detalle por serie-color
                      </Text>
                      <Box minHeight=".25rem" />
                      <Text variant="bodySm" tone="subdued">
                        Datos completos por familia: eficacia, descuentos y rentabilidad. <br />
                        Descarga en PDF o Excel para un análisis más detallado. <br />
                        Click en cualquier cabecera para ordenar por esa columna.
                      </Text>
                      <VerticalSpace />
                      <SeriesTable
                        rows={data.data}
                        metadata={data.metadata}
                        timezone={timezone}
                        sortBy={sortBy}
                        sortDir={sortDir}
                        onSort={handleSort}
                      />
                      <Box padding="3">
                        <Text variant="bodySm" tone="subdued">
                          {`${data.metadata.count} familias en la consulta`}
                        </Text>
                      </Box>
                    </Box>
                  </Card>
                  <Box minHeight=".5rem" />
                  <Card>
                    <Box padding="4">
                      <Text variant="headingMd" as="h3">
                        Series destacadas
                      </Text>
                      <Box minHeight=".25rem" />
                      <Text variant="bodySm" tone="subdued">
                        Las 10 series con mejor y peor eficacia total del informe — vistazo rápido a los extremos del
                        catálogo.
                      </Text>
                      <VerticalSpace />
                      {/* Padding extra alrededor del bloque para compactar
                          visualmente las dos columnas dentro del Card. */}
                      <Box padding="8">
                        <EfficacyHighlights rows={data.data} />
                      </Box>
                    </Box>
                  </Card>
                </>
              )}

              {tabIdx === 1 && (
                <>
                  <Box minHeight=".5rem" />
                  <AnalysisView
                    rows={data.data}
                    genderFilterActive={genderFilterActive}
                    showSub={showSub}
                    campaignLabel={campaignLabel}
                  />
                </>
              )}

              {tabIdx === 2 && (
                <>
                  <Box minHeight=".5rem" />
                  <Card>
                    <Box padding="4">
                      <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                        <EfficacySummary metadata={data.metadata} rows={data.data} />
                      </div>
                    </Box>
                  </Card>
                  {/* Card "Análisis del stock restante" — combina el
                      radar de reparto por categoría y los escenarios
                      de liquidación con descuentos. Sólo aparece si
                      queda stock en almacén; los escenarios además
                      requieren rendimiento total negativo (no tiene
                      sentido simular descuentos si ya estás en
                      beneficio). */}
                  {data.metadata.totalRemainUnits > 0 && (
                    <>
                      <Box minHeight=".5rem" />
                      <Card>
                        <Box padding="4">
                          <div style={{ maxWidth: 1100, margin: '0 auto' }}>
                            <Text variant="headingMd" as="h3">
                              Análisis del stock restante
                            </Text>
                            <Box minHeight="1.5rem" />
                            <TotalizeNumbers
                              rows={[
                                {
                                  label: 'Stock disponible',
                                  value: `${formatUnits(data.metadata.totalRemainUnits)} Uds.`,
                                },
                                { label: 'Valor contable almacén', value: fmt$(data.metadata.totalRemainValueCI) },
                                {
                                  label: '% sobre compra',
                                  value:
                                    data.metadata.totalBuyValue > 0
                                      ? pct(data.metadata.totalRemainValueCI / data.metadata.totalBuyValue)
                                      : '—',
                                },
                                {
                                  label: 'Beneficio operativo',
                                  value: `${fmt$(profit)}`,
                                },
                                {
                                  label: 'Punto neutro',
                                  value:
                                    neutralDiscount > 0
                                      ? `Dto. -${(neutralDiscount * 100).toFixed(1)}% PVP`
                                      : 'Rentable',
                                },
                              ]}
                            />
                            <Box minHeight="1rem" />
                            <Divider />
                            <VerticalSpace />
                            <WarehouseRadar
                              rows={data.data}
                              genderFilterActive={genderFilterActive}
                              showSub={showSub}
                            />
                            {profit < 0 && (
                              <>
                                <VerticalSpace />
                                <Divider />
                                <VerticalSpace size="large" />
                                <Text variant="headingSm" as="h4">
                                  Escenarios de liquidación
                                </Text>
                                <Box minHeight=".25rem" />
                                <Text variant="bodySm" tone="subdued">
                                  Qué pasaría con el rendimiento final si liquidaras el stock que queda con distintos
                                  descuentos sobre el PVP medio de referencia.
                                </Text>
                                <VerticalSpace size="medium" />
                                <Text variant="bodySm" tone="subdued">
                                  {`El beneficio actual de operación es `}
                                  <b>{fmt$(profit)}</b>.
                                </Text>
                                {neutralMsg}
                                <VerticalSpace />
                                <DiscountScale m={data.metadata} />
                              </>
                            )}
                          </div>
                        </Box>
                      </Card>
                    </>
                  )}
                </>
              )}
              <VerticalSpace size="xx-large" />
            </>
          )}
        </>
      )}
    </Page>
  );
};

export default ViewReportEfficacy;
